import { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import App from "./App.jsx";
import "bootstrap/dist/css/bootstrap.min.css";
import "./themes/themes.css";
import "./index.css";
import QuizHomeScreen from "./screens/Playbook/QuizHomeScreen.jsx";
import { getThemeConfig } from "./themes/themeConfig";

const SubscribeLanding = lazy(() => import("./screens/SubscribeLanding.jsx"));

// Build-time gate: the admin dashboard is only included in the bundle when
// VITE_ADMIN_DASH_ENABLED=true at build time. In prod we omit this env var,
// so Vite tree-shakes the dynamic import — the AdminDashboardScreen chunk
// is never emitted, and /admin falls through to the 404 catch-all.
// Defense in depth: the backend's /api/admin routes are also env-gated
// (see backend/server.js + isAdminDashEnabled).
const ADMIN_DASH_ENABLED =
  import.meta.env.VITE_ADMIN_DASH_ENABLED === "true";
const AdminDashboardScreen = ADMIN_DASH_ENABLED
  ? lazy(() => import("./screens/AdminDashboardScreen.jsx"))
  : null;

const ResultsScreen = lazy(() => import("./screens/ResultsScreen.jsx"));
const PrivacyScreen = lazy(() => import("./screens/PrivacyScreen.jsx"));
const TermsScreen = lazy(() => import("./screens/TermsScreen.jsx"));
const NotFound = lazy(() => import("./appwide/NotFound.jsx"));

// Build-time theme selection (set VITE_THEME=default, sanantonio, etc. during deployment)
const selectedTheme = import.meta.env.VITE_THEME || "default";
document.documentElement.dataset.theme = selectedTheme;
// Validate theme config early so missing required fields fail fast (clear error message).
getThemeConfig(selectedTheme);

// Microsoft Clarity (session replay, project oqt1n80t0b) was removed in the
// privacy audit: it recorded the quiz flow and the email capture form, it ran
// before any consent existed, and its first-party equivalent already exists in
// our own quiz analytics. Do not reintroduce session replay on those flows
// without consent gating and input masking.

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<App />}>
      <Route index={true} path="/" element={<QuizHomeScreen />} />
      {ADMIN_DASH_ENABLED && (
        <Route path="/admin" element={<AdminDashboardScreen />} />
      )}
      <Route path="/results" element={<ResultsScreen />} />
      <Route path="/terms" element={<TermsScreen />} />
      <Route path="/privacy" element={<PrivacyScreen />} />
      <Route path="/sublanding" element={<SubscribeLanding />} />
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

// Standard React 18 initialization
const container = document.getElementById("root");
if (container) {
  // Use createRoot (idempotent in React 18 only if checking for existing root, 
  // but here we just re-create on HMR which causes the warning but works)
  ReactDOM.createRoot(container).render(
    /* <React.StrictMode> */
    <Suspense fallback={null}>
      <RouterProvider router={router} />
    </Suspense>,
  );
}
